/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */
import { Box, Header, IconButton, Image, LoadingOverlay, Typography } from '@astro-ui/components'
import styled from 'styled-components'
import theme from '@astro-ui/theme'
import { useEffect } from 'react'
import { useOnDestroy } from 'utils/customHooks/useLifeCycleHooksAndRouter'
import { useAppSelector } from 'store'
import SuggestionIcon from 'assets/images/petunjuk.png'
import { Close } from '@astronautsid/wpe-icons'
import useLayout from './useLayout'
import { sidebarMenuList } from './constants'

type LayoutPropsType = {
  isLoading: boolean
  children: React.ReactNode
  RightPanel?: React.ReactNode
  RightHeader?: React.ReactNode
}

const Layout = ({ isLoading, children, RightPanel, RightHeader }: LayoutPropsType) => {
  const isSidebarOpen = useAppSelector((state) => state.app.layout.isSidebarOpen)

  const {
    observersRef,
    activeSection,
    sidebarMenu,
    setSidebarMenu,
    setActiveSection,
    scrollToSection,
    showSuggestion,
    handleGoBack,
    handleShowSuggestion,
  } = useLayout()

  useEffect(() => {
    setSidebarMenu(sidebarMenuList)
  }, [])

  useEffect(() => {
    if (sidebarMenu.length) {
      sidebarMenu.forEach((item) => {
        const section = document.getElementById(item.id)
        if (!section) return

        const observer = new IntersectionObserver(
          (entries) => {
            const visibleEntries = entries.filter((e) => e.isIntersecting)

            if (!visibleEntries.length) return

            const topMostSection = visibleEntries.reduce((prev, curr) =>
              Math.abs(curr.boundingClientRect.top) < Math.abs(prev.boundingClientRect.top)
                ? curr
                : prev,
            )

            setActiveSection(topMostSection.target.id)
          },
          {
            root: null,
            threshold: 0.1,
            rootMargin: '-30px 0px -70% 0px',
          },
        )

        observer.observe(section)
        observersRef.current = [...observersRef.current, observer]
      })
    }
  }, [sidebarMenu])

  useOnDestroy(() => {
    observersRef.current.forEach((obs) => obs.disconnect())
  }, [])

  return (
    <Box>
      <LoadingOverlay isLoading={isLoading} />

      <Header
        title="Buat SO Baru"
        subtitle="Buat permintaan pengiriman stok ke Hub"
        withBackButton
        handleGoBack={handleGoBack}
        RightComponent={RightHeader}
      />

      <Box style={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
        <Box display="flex" gap="24px">
          {!isSidebarOpen && (
            <SidebarWrapper>
              <SidebarContent>
                {sidebarMenu.map((item) => (
                  <SidebarItem
                    key={item.id}
                    active={activeSection === item.id}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <Typography variant="headline-small">{item.title}</Typography>
                    <Typography color={theme.palette.text.secondary} variant="body2">
                      {item.subtitle}
                    </Typography>
                  </SidebarItem>
                ))}
              </SidebarContent>

              {showSuggestion && (
                <Box
                  position="sticky"
                  top="206px"
                  height="fit-content"
                  overflow="hidden"
                  display="flex"
                  p="12px"
                  border="1px solid"
                  borderRadius="8px"
                  borderColor="#FFEBB6"
                  bg="#FFF8E7"
                  mt="16px"
                >
                  <Box display="flex" gap="8px">
                    <Image src={SuggestionIcon} height={24} />
                    <Typography variant="caption-default">
                      <b>Isi detail dasar</b> dan <b>tambahkan produk</b> yang mau dikirim ke Hub.
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => handleShowSuggestion(false)}
                    sx={{
                      width: 28,
                      height: 28,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Close size={16} />
                  </IconButton>
                </Box>
              )}
            </SidebarWrapper>
          )}

          <Box width="100%" flex={1} overflow="hidden">
            <Box display="flex" flexDirection="column" gap="20px">
              {children}
              <Box minHeight={550} />
            </Box>
          </Box>

          {RightPanel && (
            <SidebarWrapper>
              <RightPanelContent>{RightPanel}</RightPanelContent>
            </SidebarWrapper>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout

const SidebarWrapper = styled(Box)`
  position: 'relative';
  width: 320px;
  @media (max-width: ${({ theme: { breakpoints } }) => breakpoints.md}) {
    display: none;
  }
`

const RightPanelContent = styled.nav`
  position: sticky;
  top: 24px;
  height: fit-content;
  overflow: hidden;
`

const SidebarContent = styled.nav`
  background-color: ${({ theme: { palette } }) => palette.background.paper};
  position: sticky;
  top: 24px;
  height: fit-content;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme: { palette } }) => palette.divider};
`

const SidebarItem = styled.div<{ active?: boolean }>`
  position: relative;
  padding: 16px 24px;
  cursor: pointer;
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme: { palette } }) => palette.divider};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    pointer-events: none;
    width: 0;
    background-color: ${({ theme: { palette } }) => palette.primary.main};
  }

  ${({ active, theme: { colors } }) =>
    active &&
    `
    background-color: ${colors.bgLightBlue};

    &::before {
      width: 4px;
    }
  `}
`
